import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import cleanText from 'utils/cleanText';
import { getNewOperationDate } from 'utils/getNewOperationDate';

import operationTypes from 'constants/operationTypes';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import { Movement, Transaction, HistoryMovement, SelectorOption } from 'types';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { useGetPartnerOptionsForOperationModal } from 'modules/transactions/hooks/useGetPartnerOptionsForOperationModal';
import { useGetHoldingClassOptionsForOperationModal } from 'modules/transactions/hooks/useGetHoldingClassOptionsForOperationModal';
import { useGetLastOperationDate } from 'modules/transactions/hooks/useGetLastOperationDate';
import { addOperation } from 'modules/transactions/redux/operationActions';

import movementTypes from 'modules/transactions/constants/movementTypes';
import transactionTypes from 'constants/transactionTypes';
import asyncActionSwal from 'utils/asyncActionSwal';
import { getHistoryMovements } from '../utils/getHistoryMovements';
import { getMovements } from '../utils/getMovements';
import { getTransactions } from '../utils/getTransactions';
import { withUnsavedCheck } from '../utils/withUnsavedCheck';

import { ReclassificationModalContextType } from '../types/ReclassificationModalContextType';
import { reclassificationModalInitValue } from '../constants/reclassificationModalInitValue';
import { useGetRanges } from '../hooks/useGetRanges';

const ReclassificationModalContext =
  createContext<ReclassificationModalContextType>(
    reclassificationModalInitValue
  );

export const ReclassificationModalProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const user = useSelector((state: State) => state.user);

  const [date, setDate] = useState<string>('');
  const [scriptureView, setScriptureView] = useState<boolean>(true);
  const [comments, setComments] = useState<string>('');
  const [file, setFile] = useState<File | undefined>();
  const [notaryName, setNotaryName] = useState<string>('');
  const [notaryProtocol, setNotaryProtocol] = useState<string>('');
  const [notaryCity, setNotaryCity] = useState<string>('');
  const [isReclassificationByClass, setIsReclassificationByClass] =
    useState<boolean>(true);
  const [isEntireClassReclassification, setIsEntireClassReclassification] =
    useState<boolean>(false);
  const [selectedPartner, setSelectedPartner] = useState<string>('');
  const [selectedNewHoldingClass, setSelectedNewHoldingClass] =
    useState<string>('');
  const [selectedNewHoldingClassOption, setSelectedNewHoldingClassOption] =
    useState<SelectorOption | null>(null);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [disabledKeys, setDisabledKeys] = useState<string[]>([]);
  const [historyMovements, setHistoryMovements] = useState<HistoryMovement[]>(
    []
  );
  const [movements, setMovements] = useState<Movement[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: partnerOptionsData, actions: partnerOptionsActions } =
    useGetPartnerOptionsForOperationModal({
      societyId: actualSociety?.['_id'],
      onlyActiveFirst: true,
      isSingleSelect: true,
    });

  const { data: holdingClassOptionsData, actions: holdingClassOptionsActions } =
    useGetHoldingClassOptionsForOperationModal({
      societyId: actualSociety?._id,
    });

  const { lastOperationDate, formattedLastOperationDate } =
    useGetLastOperationDate(actualSociety?._id).data;

  const {
    data: rangesData,
    actions: rangesActions,
    isLoading: isLoadingRanges,
  } = useGetRanges({
    holdingClassId: holdingClassOptionsData.selectedHoldingClass,
    partnerId: selectedPartner,
    page,
    rowsPerPage,
    isReclassificationByClass,
    isEntireClassReclassification,
  });

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeDate = (event: ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
  };

  const handleChangeComments = (value: string) => {
    setComments(value);
  };

  const handleChangeFile = (file: File) => {
    setFile(file);
  };

  const onChangeNotaryName = (value: string) => {
    setNotaryName(value);
  };

  const onChangeNotaryProtocol = (value: string) => {
    setNotaryProtocol(value);
  };

  const onChangeNotaryCity = (value: string) => {
    setNotaryCity(value);
  };

  const handleChangeIsReclassificationByClass = () => {
    withUnsavedCheck({
      editedRanges: rangesData.editedRanges,
      action: () => {
        setIsReclassificationByClass((prev) => !prev);
        setIsEntireClassReclassification(false);
        setSelectedNewHoldingClass('');
        setSelectedNewHoldingClassOption(null);
        rangesActions.handleResetValues();
        setPage(1);
      },
      translationFunction: t,
    });
  };

  const handleChangePartner = (selectedOption: SelectorOption) => {
    withUnsavedCheck({
      editedRanges: rangesData.editedRanges,
      action: () => {
        setSelectedPartner(selectedOption?.value || '');
        partnerOptionsActions.handleChangeSelectedPartnerFirstOption(
          selectedOption
        );
      },
      translationFunction: t,
    });
  };

  const handleChangeHoldingClass = (selectedOption: SelectorOption) => {
    withUnsavedCheck({
      editedRanges: rangesData.editedRanges,
      action: () => {
        holdingClassOptionsActions.handleClassOptionChange(selectedOption);
        rangesActions.handleResetValues();
        setPage(1);
      },
      translationFunction: t,
    });
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
  };

  const handleChangeIsEntireClassReclassification = () => {
    withUnsavedCheck({
      editedRanges: rangesData.editedRanges,
      action: () => {
        setIsEntireClassReclassification((prev) => !prev);
        rangesActions.handleResetValues();
        setPage(1);
      },
      translationFunction: t,
    });
  };

  const handleChangeNewHoldingClass = (selectedOption: SelectorOption) => {
    setSelectedNewHoldingClassOption(selectedOption);
    setSelectedNewHoldingClass(selectedOption?.value || '');
  };

  const reclasifyEntireClass = () => {
    const newMovement: Movement = {
      shareFrom: 0,
      shareTo: 0,
      shareClass: holdingClassOptionsData.selectedHoldingClass || '',
      newShareFrom: 0,
      newShareTo: 0,
      newShareClass: selectedNewHoldingClass,
      movementType: movementTypes.ENTIRE_CLASS_RECLASSIFICATION,
    };

    const newTransaction: Transaction = {
      shareFrom: 0,
      shareTo: 0,
      shareClass: holdingClassOptionsData.selectedHoldingClass || '',
      newShareFrom: 0,
      newShareTo: 0,
      newShareClass: selectedNewHoldingClass,
      transactionType: transactionTypes.ENTIRE_CLASS_RECLASSIFICATION,
    };

    const historyMovement: HistoryMovement = {
      from: 0,
      to: 0,
      holdingClassName:
        holdingClassOptionsData.selectedHoldingClassOption?.label || '',
      newHoldingClassName: selectedNewHoldingClassOption?.label || '',
      movementType: movementTypes.ENTIRE_CLASS_RECLASSIFICATION,
    };

    setMovements([newMovement]);
    setTransactions([newTransaction]);
    setHistoryMovements([historyMovement]);

    setDisabledKeys([
      `entire-class-${holdingClassOptionsData.selectedHoldingClass}`,
    ]);
  };

  const handleSaveReclassifyEntireClassMovement = () => {
    if (historyMovements.length > 0) {
      asyncActionSwal({
        swalTitle: t('AllPreviousMovementsWillBeLost'),
        swalElementName: '',
        swalInfoText: '',
        swalConfirmButtonText: t('Continue'),
        callback: reclasifyEntireClass,
      });
    } else {
      reclasifyEntireClass();
    }
  };

  const handleSaveMovement = () => {
    if (isEntireClassReclassification) {
      handleSaveReclassifyEntireClassMovement();
      return;
    }
    const movements = getMovements(rangesData.editedRanges);
    const transactions = getTransactions(rangesData.editedRanges);
    const historyMovements = getHistoryMovements({
      editedRanges: rangesData.editedRanges,
      holdingClassName:
        holdingClassOptionsData.selectedHoldingClassOption?.label || '',
    });

    setMovements((prev) => [...prev, ...movements]);
    setTransactions((prev) => [...prev, ...transactions]);
    setHistoryMovements((prev) => [...prev, ...historyMovements]);

    setDisabledKeys((prev) => [
      ...prev,
      ...rangesData.editedRanges.map((r) => r.key),
    ]);

    rangesActions.handleSaveRanges();
  };

  const handleSaveReclassification = async () => {
    if (scriptureView) {
      setScriptureView(false);
    } else {
      try {
        setIsLoading(true);

        let uploadedDocument = null;

        if (file) {
          const newDocument = {
            file,
            date: new Date(date),
            size: file.size,
            name: operationTypes.CONTRIBUTION_FILENAME,
            author: user['_id'],
            society: actualSociety['_id'],
            fileType: fileTypes[file.type as keyof typeof fileTypes],
            category: documentTypes.SCRIPTURES,
            description: t('DocumentLinkedToOperation'),
          };
          uploadedDocument = await store.dispatch(addDocument(newDocument));
        }

        const newOperationDate = getNewOperationDate(lastOperationDate, date);

        const reclassificationData = {
          movements,
          transactions,
          date: newOperationDate,
          nominalValue: actualSociety.nominalValue,
          society: actualSociety['_id'],
          user: user['_id'],
          documents: uploadedDocument?.['_id'] ? [uploadedDocument['_id']] : [],
          comments: cleanText(comments),
          operationType: operationTypes.RECLASSIFICATION,
        };

        await store.dispatch(addOperation(reclassificationData));
        setIsLoading(false);

        successSwal(t('ReclassificationSuccessfullyCompleted'));
      } catch (error) {
        errorSwal(t('ErrorWhenPerformingReclassification'));
      } finally {
        handleCloseModal();
      }
    }
  };

  return (
    <ReclassificationModalContext.Provider
      value={{
        data: {
          date,
          comments,
          file,
          notaryName,
          notaryProtocol,
          notaryCity,
          isReclassificationByClass,
          isEntireClassReclassification,
          selectedPartner,
          selectedPartnerOption: partnerOptionsData.selectedPartnerFirstOption,
          partnerOptions: partnerOptionsData.partnerFirstOptions,
          selectedHoldingClass: holdingClassOptionsData.selectedHoldingClass,
          holdingClassOptions: holdingClassOptionsData.holdingClassOptions,
          selectedHoldingClassOption:
            holdingClassOptionsData.selectedHoldingClassOption,
          selectedNewHoldingClassOption,
          selectedNewHoldingClass,
          ranges: rangesData.ranges,
          totalRanges: rangesData.totalRanges,
          page,
          rowsPerPage,
          lastOperationDate: formattedLastOperationDate,
          scriptureView,
          loadingKeys: rangesData.loadingKeys,
          disabledKeys,
          editedRanges: rangesData.editedRanges,
          historyMovements,
          isLoadingRanges,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeDate,
          handleChangeComments,
          handleChangeFile,
          onChangeNotaryName,
          onChangeNotaryProtocol,
          onChangeNotaryCity,
          handleChangeIsReclassificationByClass,
          handleChangeIsEntireClassReclassification,
          handleChangePartner,
          handleSearchPartnerByTerm: partnerOptionsActions.fetchPartnersByTerm,
          handleChangeHoldingClass,
          handleChangeNewHoldingClass,
          handleFetchHoldingClassByTerm:
            holdingClassOptionsActions.handleFetchHoldingClassByTerm,
          handleChangePage,
          handleChangeRowsPerPage,
          handleEditRange: rangesActions.handleEditRange,
          handleSaveMovement,
          handleSaveReclassification,
        },
      }}
    >
      {children}
    </ReclassificationModalContext.Provider>
  );
};

export const useReclassificationModal = () => {
  const context = useContext(ReclassificationModalContext);
  if (!context) {
    throw new Error(
      'useReclassificationModal must be used within a ReclassificationModalProvider'
    );
  }
  return context;
};
